/**
 * RescueHistoryPanel — inline drawer that lists the last-N draft snapshots
 * stored in IndexedDB so a mis-clicked "Clear Draft" can be undone weeks
 * later, even after a tab reopen.
 *
 * Rendered as a compact right-side sheet (Shadcn Sheet) so it doesn't cover
 * the sorter workflow while the user browses history. Each row exposes:
 *   • when it was snapshotted (and *why* — clear/export)
 *   • how much it contains (photos × clusters)
 *   • Restore button — replaces the live draft with this snapshot
 *   • Delete button — for freeing space
 *
 * Restore is undoable via the existing sorter undo stack; the parent passes
 * `undo.push("restore history")` before calling `applyRestore`.
 */
import React, { useEffect, useState } from "react";
import { listHistorySnapshots, restoreHistorySnapshot, deleteHistorySnapshot } from "@/lib/bulkPhotoDraft";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Loader2, RotateCcw, Trash2, Clock } from "lucide-react";

const SOURCE_LABEL = {
  clear: "auto-saved before clear",
  export: "auto-saved on export",
  manual: "manual snapshot",
};

export function RescueHistoryPanel({ open, onOpenChange, onRestore }) {
  const [rows, setRows] = useState(null); // null = loading, [] = empty, [...] = loaded
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setRows(null);
    setRows(await listHistorySnapshots());
  };

  useEffect(() => {
    if (open) reload();
  }, [open]);

  const handleRestore = async (id) => {
    setBusyId(id);
    try {
      const snap = await restoreHistorySnapshot(id);
      if (!snap) {
        toast.error("couldn't read that snapshot — it may have been evicted.");
        return;
      }
      onRestore(snap);
      onOpenChange(false);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("delete this snapshot? this cannot be undone.")) return;
    setBusyId(id);
    try {
      await deleteHistorySnapshot(id);
      await reload();
      toast.success("snapshot deleted.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-linen" data-testid="bp-history-panel">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-charcoal">rescue history</SheetTitle>
          <SheetDescription className="text-espresso">
            the last three drafts, kept in this browser through clears and exports so a mis-clicked action stays recoverable.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3" data-testid="bp-history-list">
          {rows === null && (
            <p className="text-xs text-taupe flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading…</p>
          )}
          {rows && rows.length === 0 && (
            <p className="text-xs text-espresso italic">no snapshots yet. one will be captured next time you export or clear the draft.</p>
          )}
          {rows && rows.map((r) => {
            const isBusy = busyId === r.id;
            return (
              <div
                key={r.id}
                className="border border-taupe/50 bg-ivory p-3 flex flex-col gap-2"
                data-testid={`bp-history-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[0.65rem] tracking-widest uppercase text-taupe flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    <p className="text-xs text-charcoal mt-1">
                      <strong>{r.totalPhotos}</strong> photo{r.totalPhotos === 1 ? "" : "s"} across <strong>{r.totalClusters}</strong> cluster{r.totalClusters === 1 ? "" : "s"}
                    </p>
                    <p className="text-[0.6rem] tracking-widest uppercase text-brass mt-0.5">
                      {SOURCE_LABEL[r.source] || r.source}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRestore(r.id)}
                    disabled={isBusy}
                    className="border border-brass text-brass hover:bg-brass hover:text-ivory disabled:opacity-50 px-3 py-1.5 text-[0.6rem] tracking-widest uppercase flex items-center gap-1 flex-1 justify-center"
                    data-testid={`bp-history-restore-${r.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    restore
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    disabled={isBusy}
                    className="border border-taupe hover:border-red-700 hover:text-red-700 disabled:opacity-50 px-3 py-1.5 text-[0.6rem] tracking-widest uppercase flex items-center gap-1"
                    data-testid={`bp-history-delete-${r.id}`}
                    title="delete this snapshot"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
